import { field } from '../fields'
import { useEffect, useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { fetchNewClassOptions } from '../api/classAdmin'
import { Crop, Image, Trash2 } from 'lucide-react'
import { classTypePlacement, clearFraming, saveFraming } from '../api/media'
import FramingEditor from './FramingEditor'
import { CENTRED } from './StudioImage'
import MediaPicker from './MediaPicker'
import {
  assetIdFromUrl,
  deleteClassType,
  NeedsConfirmation,
  saveClassType,
  setKindGate,
  setKindVisibility,
  urlFromAssetId,
  type ClassTypeView,
} from '../api/classTypes'
/**
 * The marketing editor for one kind of class, opened in place (D-205).
 *
 * <b>This is the whole of the old "Kinds of class" screen, in context.</b> It
 * lived under a different menu from the classes it describes, so writing the
 * paragraph for Tippy Toes and checking which Tippy Toes classes it covered
 * were two screens in two menus. Here the classes are directly underneath.
 *
 * Everything on it is genuinely Tonya's — the import seeds a name and a blurb
 * so the page works on day one and then never writes to one again (D-191,
 * D-203).
 */
export default function ClassKindEditor({
  kind,
  publicClasses,
  totalClasses,
  onClose,
}: {
  kind: ClassTypeView
  publicClasses: number
  totalClasses: number
  onClose: () => void
}) {
  const queryClient = useQueryClient()

  const [form, setForm] = useState(kind)
  const [picking, setPicking] = useState(false)
  const [framing, setFraming] = useState(false)

  // Already loaded by the new-class form, so this is the cache almost always.
  const { data: options } = useQuery({
    queryKey: ['new-class-options'],
    queryFn: fetchNewClassOptions,
    staleTime: 300_000,
  })

  useEffect(() => setForm(kind), [kind])

  const save = useMutation({
    mutationFn: () =>
      saveClassType(kind.classTypeId, {
        name: form.name,
        blurb: form.blurb,
        heroMediaAssetId: form.heroMediaAssetId,
        sortOrder: form.sortOrder,
        studioProgramId: form.studioProgramId,
      }),
    onSuccess: (types) => {
      queryClient.setQueryData(['class-types'], types)
      onClose()
    },
  })

  const set = (patch: Partial<ClassTypeView>) => setForm((f) => ({ ...f, ...patch }))
  const dirty = JSON.stringify(form) !== JSON.stringify(kind)

  return (
    <div className="flex flex-col gap-3 border-t border-line bg-surface-2 px-4 py-3.5">
      <label className="flex flex-col gap-1">
        <span className="text-[12px] font-semibold text-ink-3">Name</span>
        <input
          value={form.name}
          onChange={(e) => set({ name: e.target.value })}
          className={field}
        />
        {/*
          Said because it is genuinely surprising: the import matches on a key
          she never sees, so renaming is safe (D-88).
        */}
        {kind.derivedKey && (
          <span className="text-[11.5px] text-ink-3">
            Renaming is safe &mdash; the next import still finds it.
          </span>
        )}
      </label>

      {/*
        Which programme this kind is for (D-227).

        <b>A label, and it is worth being clear that it is only a label.</b> The
        80% rule, capacity and self-registration all read the *class's* own
        programme and never this (D-191) — so the note below the box says so,
        because a control that looks like it moves classes between programmes
        and does not is exactly the sort that gets used once and mistrusted
        afterwards.
      */}
      <label className="flex flex-col gap-1">
        <span className="text-[12px] font-semibold text-ink-3">What kind of class</span>
        <select
          value={form.studioProgramId ?? ''}
          onChange={(e) => set({ studioProgramId: e.target.value || null })}
          className={field}
        >
          <option value="">Not said yet</option>
          {(options?.programs ?? []).map((p) => (
            <option key={p.id} value={p.id}>
              {p.name}
            </option>
          ))}
        </select>

        <span className="text-[11.5px] text-ink-3">
          For sorting and filtering this list. Attendance rules follow each class&rsquo;s own
          programme, not this.
        </span>

        {/*
          Reported, never corrected. Under D-51 a competitive class landing in a
          recreational kind usually means a missed dropdown in Jackrabbit — and
          the class is the thing to change, which is not this screen's job.
        */}
        {kind.classesInAnotherProgram > 0 && (
          <span className="rounded-lg bg-warn-bg px-3 py-2 text-[11.5px] text-ink-2">
            {kind.classesInAnotherProgram === 1
              ? '1 class in here belongs to a different programme.'
              : `${kind.classesInAnotherProgram} classes in here belong to a different programme.`}{' '}
            Worth a look &mdash; that is usually a missed dropdown in Jackrabbit.
          </span>
        )}
      </label>

      <label className="flex flex-col gap-1">
        <span className="text-[12px] font-semibold text-ink-3">What this kind of class is</span>
        <textarea
          value={form.blurb ?? ''}
          onChange={(e) => set({ blurb: e.target.value || null })}
          rows={3}
          placeholder="A sentence or two a parent reads before deciding."
          className={field}
        />
      </label>

      <div className="flex flex-wrap items-center gap-3">
        <button
          type="button"
          onClick={() => setPicking(true)}
          className="inline-flex items-center gap-2 rounded-lg border border-line-2 bg-surface px-3 py-2 text-[12.5px] font-semibold text-ink-2 hover:border-ink-3"
        >
          <Image size={14} strokeWidth={2.5} />
          {form.heroMediaAssetId ? 'Change photo' : 'Choose a photo'}
        </button>

        {form.heroMediaAssetId && (
          <>
            {/*
              Beside the picker, because "is it cropped badly?" is only
              answerable once a photo is chosen and in the box (D-199). Never
              required — centred is what it already does.
            */}
            <button
              type="button"
              onClick={() => setFraming((f) => !f)}
              className="inline-flex items-center gap-2 rounded-lg border border-line-2 bg-surface px-3 py-2 text-[12.5px] font-semibold text-ink-2 hover:border-ink-3"
            >
              <Crop size={14} strokeWidth={2.5} />
              {framing ? 'Done positioning' : 'Position photo'}
            </button>

            <button
              type="button"
              onClick={() => set({ heroMediaAssetId: null, heroUrl: null, heroFraming: null })}
              className="text-[12.5px] font-semibold text-ink-3 hover:text-ink"
            >
              Remove photo
            </button>
          </>
        )}
      </div>

      {/*
        Saves as you drag, on its own, rather than waiting for Save. Framing is
        a fiddly adjustment somebody repeats a few times, and losing it to a
        forgotten Save is the kind of small betrayal that stops people bothering.
      */}
      {framing && form.heroMediaAssetId && form.heroUrl && (
        <div className="rounded-xl border border-line bg-surface p-3">
          <FramingEditor
            src={form.heroUrl}
            alt={form.name}
            value={form.heroFraming ?? CENTRED}
            scope="just for this kind of class"
            onChange={(next) => {
              set({ heroFraming: next })
              void saveFraming(
                form.heroMediaAssetId!,
                next,
                classTypePlacement(kind.classTypeId),
              )
            }}
            onReset={() => {
              set({ heroFraming: CENTRED })
              void clearFraming(form.heroMediaAssetId!, classTypePlacement(kind.classTypeId))
            }}
          />
        </div>
      )}

      <KindGate kind={kind} ticked={publicClasses} totalClasses={totalClasses} />

      <KindVisibility
        classTypeId={kind.classTypeId}
        publicClasses={publicClasses}
        totalClasses={totalClasses}
        gateOpen={kind.showInPublicCatalog}
      />

      {save.isError && (
        <p role="alert" className="text-[12.5px] text-crit">
          {(save.error as Error).message}
        </p>
      )}

      <div className="flex flex-wrap items-center gap-2">
        <button
          type="button"
          disabled={!dirty || save.isPending || form.name.trim().length === 0}
          onClick={() => save.mutate()}
          className="rounded-lg bg-accent px-3.5 py-2 text-[12.5px] font-semibold text-on-accent disabled:opacity-40"
        >
          {save.isPending ? 'Saving...' : dirty ? 'Save' : 'Saved'}
        </button>

        <button
          type="button"
          onClick={onClose}
          className="text-[12.5px] font-semibold text-ink-3 hover:text-ink"
        >
          {dirty ? 'Discard' : 'Close'}
        </button>

        <RemoveKind kind={kind} totalClasses={totalClasses} onRemoved={onClose} />
      </div>

      {picking && (
        <MediaPicker
          current={urlFromAssetId(form.heroMediaAssetId)}
          onChoose={(url) => {
            set({ heroMediaAssetId: assetIdFromUrl(url), heroUrl: url })
            setPicking(false)
          }}
          onClose={() => setPicking(false)}
        />
      )}
    </div>
  )
}

/**
 * Removing a kind of class (D-216).
 *
 * <b>Two steps when it holds classes, one when it does not.</b> An empty kind
 * — the six competitive ones a pre-D-195 importer left behind — is a row nobody
 * wants and confirming its removal is ceremony. A kind with seven classes in it
 * is a different act: they lose their heading and drop to "More classes" on the
 * public page, silently, so the count has to be seen before it happens.
 *
 * <b>The classes are never removed with it.</b> Losing a class because somebody
 * tidied up the catalog is far worse than an unheaded row (D-191).
 */
function RemoveKind({
  kind,
  totalClasses,
  onRemoved,
}: {
  kind: ClassTypeView
  totalClasses: number
  onRemoved: () => void
}) {
  const queryClient = useQueryClient()
  const [asked, setAsked] = useState(false)

  const remove = useMutation({
    mutationFn: (confirm: boolean) => deleteClassType(kind.classTypeId, confirm),
    onSuccess: (types) => {
      queryClient.setQueryData(['class-types'], types)
      queryClient.invalidateQueries({ queryKey: ['admin-classes'] })

      // Deleting a kind un-files its classes, so an open panel is showing a
      // kind that no longer exists (D-496).
      queryClient.invalidateQueries({ queryKey: ['admin-class'] })
      onRemoved()
    },
    onError: (error) => {
      // The server is the one that counts the classes, so the question comes
      // from it rather than from the screen guessing.
      if (error instanceof NeedsConfirmation) setAsked(true)
    },
  })

  if (asked) {
    return (
      <span className="flex flex-wrap items-center gap-2 rounded-lg border border-crit bg-crit-bg px-3 py-1.5">
        <span className="text-[12px] text-ink-2">
          {totalClasses} {totalClasses === 1 ? 'class stays' : 'classes stay'} on the website
          without a heading. Remove &ldquo;{kind.name}&rdquo; anyway?
        </span>

        <button
          type="button"
          disabled={remove.isPending}
          onClick={() => remove.mutate(true)}
          className="rounded-md bg-crit px-2.5 py-1 text-[12px] font-semibold text-on-crit disabled:opacity-40"
        >
          {remove.isPending ? 'Removing...' : 'Remove it'}
        </button>

        <button
          type="button"
          onClick={() => setAsked(false)}
          className="text-[12px] font-semibold text-ink-3 hover:text-ink"
        >
          Keep it
        </button>
      </span>
    )
  }

  return (
    <button
      type="button"
      disabled={remove.isPending}
      onClick={() => remove.mutate(false)}
      className="ml-auto flex items-center gap-1.5 text-[12.5px] font-semibold text-crit hover:underline disabled:opacity-40"
    >
      <Trash2 size={13} strokeWidth={2.5} />
      Remove this kind
    </button>
  )
}

/**
 * The kind's own gate onto the public page (D-261, amending D-204).
 *
 * <b>D-204 removed a switch from here, and this is not that switch.</b> The old
 * one hid the heading and left its classes listed under &ldquo;More
 * classes&rdquo; — it read as &ldquo;take this off the site&rdquo; and took
 * nothing off the site. This one decides whether the classes reach the catalog
 * at all.
 *
 * <b>It is still a second control, which is the objection D-204 actually
 * made.</b> The answer is not to hide the fact: the panel below says how many
 * classes are ticked, this says whether any of them can get through, and the
 * class panel says which of the two is stopping it. A switch that is honest
 * about a second switch is a different thing from one that pretends to be the
 * only one.
 *
 * It exists because competitive used to be excluded by a hardcoded rule no
 * admin could reach — so there was no way to put a class on the site to check
 * that any of this works.
 */
function KindGate({
  kind,
  ticked,
  totalClasses,
}: {
  kind: ClassTypeView
  ticked: number
  totalClasses: number
}) {
  const queryClient = useQueryClient()

  const apply = useMutation({
    mutationFn: (open: boolean) => setKindGate(kind.classTypeId, open),
    onSuccess: (types) => {
      queryClient.setQueryData(['class-types'], types)

      // The catalog is refreshed server-side (D-210); this is the admin list,
      // which shows each class's own flag alongside.
      queryClient.invalidateQueries({ queryKey: ['admin-classes'] })

      // And the panel, which names the kind and reports why a class is off
      // the website — both of which this just changed (D-261, D-496).
      queryClient.invalidateQueries({ queryKey: ['admin-class'] })
    },
  })

  const open = kind.showInPublicCatalog

  return (
    <div className="flex flex-col gap-1.5 rounded-lg border border-line-2 bg-surface px-3 py-2.5">
      <label className="flex items-start gap-2.5">
        <input
          type="checkbox"
          checked={open}
          disabled={apply.isPending}
          onChange={(e) => apply.mutate(e.target.checked)}
          className="mt-0.5 size-4 shrink-0 accent-[var(--color-accent)]"
        />
        <span className="min-w-0">
          <span className="block text-[13.5px] font-semibold">Show this kind on the website</span>
          <span className="mt-0.5 block text-[12px] text-ink-3">
            {open ? (
              <>
                Its classes can appear, and each one still has its own switch below.
              </>
            ) : (
              <>
                Nothing filed under this kind appears on the website, whatever its own switch
                says.
              </>
            )}
          </span>
        </span>
      </label>

      {/*
        The state that would otherwise be a mystery: classes ticked for the site
        behind a shut gate. Somebody looking at "7 on the site" in the panel
        below and an empty public page needs the two facts in one place.
      */}
      {!open && ticked > 0 && (
        <p className="text-[12px] text-warn">
          {ticked === 1
            ? '1 class here is ticked for the website'
            : `${ticked} classes here are ticked for the website`}{' '}
          and stays off it until this is on.
        </p>
      )}

      {/*
        Why it is off, when nobody chose that (D-261). A kind arrives shut from
        the import and reads exactly like one Tonya turned off by hand.

        Two different reasons, and saying the wrong one is worse than saying
        nothing. A genuinely competitive programme is placement-by-tryout; a
        kind that merely *reads* as competitive is D-51's missed dropdown — Tiny
        Tutus is the real example, a toddler programme whose Cat 1 nobody filled
        in. Told it was "competitive", she would go looking for a team.
      */}
      {!open && !kind.visibilityIsOurs && (
        <p className="text-[12px] text-ink-3">
          {kind.programmeIsPublic === false ? (
            <>
              Off by default because this is competitive &mdash; placement is by tryout, so
              teams are not listed for families to join. Turn it on if you want it shown.
            </>
          ) : (
            <>
              Off by default because its classes are not marked as recreational in Jackrabbit.
              If this is a class families should be able to join, turn it on.
            </>
          )}
        </p>
      )}

      {/*
        Keyed off the programme's own flag, never off its name — programme names
        are editable reference data, and `name === 'Competitive'` is a rule that
        stops working silently the day somebody renames it.
      */}
      {open && kind.programmeIsPublic === false && (
        <p className="text-[12px] text-warn">
          This is a competitive kind and its classes are on the public website. Placement is
          normally by tryout rather than sign-up.
        </p>
      )}

      {totalClasses === 0 && (
        <p className="text-[12px] text-ink-3">
          Nothing is filed under it yet, so it does not appear either way.
        </p>
      )}

      {apply.isError && (
        <p role="alert" className="text-[12px] text-crit">
          {(apply.error as Error).message}
        </p>
      )}
    </div>
  )
}

/**
 * Showing or hiding a whole programme, by changing its classes (D-204).
 *
 * <b>The bulk action, not the gate.</b> This writes the flag on every class in
 * the kind; {@link KindGate} above writes one field on the kind itself and
 * leaves the classes alone. Both are needed, and they answer different
 * questions — "put these seven on the site" against "may this kind reach the
 * site at all".
 *
 * Said as a count and an action rather than a checkbox, because "6 of 7 are
 * public" is a state a checkbox cannot represent, and the real data has kinds
 * in exactly that state.
 */
function KindVisibility({
  classTypeId,
  publicClasses,
  totalClasses,
  gateOpen,
}: {
  classTypeId: string
  publicClasses: number
  totalClasses: number
  gateOpen: boolean
}) {
  const queryClient = useQueryClient()

  const apply = useMutation({
    mutationFn: (visible: boolean) => setKindVisibility(classTypeId, visible),
    onSuccess: (types) => {
      queryClient.setQueryData(['class-types'], types)
      queryClient.invalidateQueries({ queryKey: ['admin-classes'] })

      // The panel explains which switch is keeping a class off the page
      // (D-261), and this is that switch (D-496).
      queryClient.invalidateQueries({ queryKey: ['admin-class'] })
    },
  })

  if (totalClasses === 0) return null

  const all = publicClasses === totalClasses
  const none = publicClasses === 0

  return (
    <div className="flex flex-wrap items-center gap-2.5 rounded-lg border border-line-2 bg-surface px-3 py-2.5">
      {/*
        Worded against the gate, because "all 7 are on the public page" is
        simply false when the kind is switched off — and a panel that says it
        anyway is the control-that-lies problem D-204 was written about, moved
        into a sentence.
      */}
      <span className="min-w-0 flex-1 text-[12.5px] text-ink-2">
        {all && (
          <>
            All {totalClasses} of its classes are ticked
            {gateOpen ? <> and on the public page.</> : <>, but the kind is off.</>}
          </>
        )}
        {none && <>None of its classes are ticked, so it does not appear at all.</>}
        {!all && !none && (
          <>
            {publicClasses} of {totalClasses} are ticked
            {gateOpen ? <> and on the public page.</> : <>, and the kind is off.</>}
          </>
        )}
      </span>

      <button
        type="button"
        disabled={all || apply.isPending}
        onClick={() => apply.mutate(true)}
        className="rounded-lg border border-line-2 px-3 py-1.5 text-[12px] font-semibold text-ink-2 hover:border-ink-3 disabled:opacity-40"
      >
        Show all
      </button>

      <button
        type="button"
        disabled={none || apply.isPending}
        onClick={() => apply.mutate(false)}
        className="rounded-lg border border-line-2 px-3 py-1.5 text-[12px] font-semibold text-ink-2 hover:border-ink-3 disabled:opacity-40"
      >
        Hide all
      </button>
    </div>
  )
}
